import { api } from '../api';
import { ApiError } from '../api.utils';
import { appModeHeader } from '../utils/appMode';
import { toQuery } from '../utils/query';
import type {
  AddCartProductProps,
  AddCartProductResponse,
  AddLoyaltyRedemptionProps,
  ApplyPromoCodeProps,
  CartResponse,
  CheckoutProps,
  CheckoutResponse,
  CreateCartProps,
  DeleteCartProps,
  FetchCartProps,
  FetchUserCartProps,
  RemoveCartProductProps,
  RemoveLoyaltyRedemptionProps,
  UpdateCartProductProps,
  UpdateCartProductResponse,
  UpdateCartProps,
} from './cart.types';

const BASE_URL = '/v3/carts';

const browserUserAgent = () =>
  typeof window !== 'undefined' ? window.navigator.userAgent : undefined;

const cartUrls = {
  cart: (uuid: string) => `${BASE_URL}/${uuid}`,
  carts: () => BASE_URL,
  userCart: () => '/v3/users/cart',
  checkout: (cartUuid: string) => `${BASE_URL}/${cartUuid}/checkout`,
  promoCode: (cartUuid: string) => `${BASE_URL}/${cartUuid}/promo_code`,
  cartProducts: (cartUuid: string) => `${BASE_URL}/${cartUuid}/cart_products`,
  cartProduct: (cartUuid: string, productId: number) =>
    `${BASE_URL}/${cartUuid}/cart_products/${productId}`,
  loyaltyRedemptions: (cartUuid: string) =>
    `${BASE_URL}/${cartUuid}/loyalty_redemptions`,
  loyaltyRedemption: (cartUuid: string, redemptionId: string) =>
    `${BASE_URL}/${cartUuid}/loyalty_redemptions/${redemptionId}`,
};

export const fetchCart = async ({
  uuid,
  jwt,
  appMode,
  deliveryAddress,
  paymentMethod,
}: FetchCartProps) => {
  const url = `${cartUrls.cart(uuid)}${toQuery({
    payment_method: paymentMethod,
    delivery_address: deliveryAddress,
  })}`;

  const response = await api.get<CartResponse>(
    url,
    jwt,
    appModeHeader(appMode)
  );
  return response.cart;
};

export const fetchUserCart = async ({
  appMode,
  storeId,
  uuid,
  jwt,
}: FetchUserCartProps) => {
  const url = `${cartUrls.userCart()}${toQuery({
    store_id: storeId,
    uuid,
  })}`;

  try {
    const response = await api.get<CartResponse>(
      url,
      jwt,
      appModeHeader(appMode)
    );
    return response.cart;
  } catch (error) {
    // The backend's V3::Users::CartsController#set_cart calls render_not_found
    // (HTTP 404) when the user has no pending cart — a normal state for brand-new
    // users or cleared localStorage, not a failure. Treat only that case as an
    // empty cart; let everything else (401, 500, network) propagate.
    if (error instanceof ApiError && error.response.status === 404) {
      return null;
    }
    throw error;
  }
};

export const createCart = async ({
  appMode,
  storeId,
  products,
  jwt,
  reservationMode,
  tags,
  promoCode,
  externalSource,
  headlessCheckoutPartnerId,
  deviceId,
  userAgent,
}: CreateCartProps) => {
  const body = {
    store_id: storeId,
    products,
    reservation_mode: reservationMode,
    tags,
    promo_code: promoCode,
    user_agent: userAgent ?? browserUserAgent(),
    external_source: externalSource,
    headless_checkout_partner_id: headlessCheckoutPartnerId,
    j_device_id: deviceId,
  };

  const response = await api.post<CartResponse>(
    cartUrls.carts(),
    body,
    jwt,
    appModeHeader(appMode)
  );
  return response.cart;
};

export const updateCart = async ({
  uuid,
  jwt,
  appMode,
  ...updates
}: UpdateCartProps) => {
  const body = {
    ...(updates.reservationMode !== undefined && {
      reservation_mode: updates.reservationMode,
    }),
    ...(updates.paymentMethod !== undefined && {
      payment_method: updates.paymentMethod,
    }),
    ...(updates.promotionsOptOut !== undefined && {
      promotions_opt_out: updates.promotionsOptOut,
    }),
    ...(updates.userId !== undefined && { user_id: updates.userId }),
    ...(updates.userGroupSpecialId !== undefined && {
      user_group_special_id: updates.userGroupSpecialId,
    }),
    ...(updates.message !== undefined && { message: updates.message }),
    ...(updates.deliveryStartedAt !== undefined && {
      delivery_started_at: updates.deliveryStartedAt,
    }),
    ...(updates.deliveryFinishedAt !== undefined && {
      delivery_finished_at: updates.deliveryFinishedAt,
    }),
    ...(updates.deliveryAddress !== undefined && {
      delivery_address: updates.deliveryAddress,
    }),
  };

  const response = await api.patch<CartResponse>(
    cartUrls.cart(uuid),
    body,
    jwt,
    appModeHeader(appMode)
  );
  return response.cart;
};

export const deleteCart = async ({ uuid, jwt, appMode }: DeleteCartProps) => {
  await api.delete(cartUrls.cart(uuid), jwt, appModeHeader(appMode));
};

export const addCartProduct = async ({
  cartUuid,
  storeId,
  product,
  jwt,
  appMode,
  tags,
  deviceId,
  userAgent,
}: AddCartProductProps) => {
  const body = {
    cart_uuid: cartUuid,
    store_id: storeId,
    product,
    tags,
    user_agent: userAgent ?? browserUserAgent(),
    j_device_id: deviceId,
  };

  return api.post<AddCartProductResponse>(
    cartUrls.cartProducts(cartUuid || ''),
    body,
    jwt,
    appModeHeader(appMode)
  );
};

export const updateCartProduct = async ({
  cartUuid,
  productId,
  priceId,
  count,
  jwt,
  appMode,
}: UpdateCartProductProps) => {
  const body = {
    product: { count, price_id: priceId },
  };

  // The controller resolves the weight line from a TOP-LEVEL `price_id`; nested
  // under `product` it would address an arbitrary weight of the same product.
  return api.patch<UpdateCartProductResponse>(
    `${cartUrls.cartProduct(cartUuid, productId)}${toQuery({
      price_id: priceId,
    })}`,
    body,
    jwt,
    appModeHeader(appMode)
  );
};

export const removeCartProduct = async ({
  cartUuid,
  productId,
  priceId,
  jwt,
  appMode,
}: RemoveCartProductProps) => {
  await api.delete(
    `${cartUrls.cartProduct(cartUuid, productId)}${toQuery({
      price_id: priceId,
    })}`,
    jwt,
    appModeHeader(appMode)
  );
};

export const checkout = async ({
  cartUuid,
  jwt,
  appMode,
  ...params
}: CheckoutProps) => {
  const body = {
    reservation_mode: params.reservationMode,
    payment_method: params.paymentMethod,
    customer: params.customer,
    recaptcha_token: params.recaptchaToken,
    ...(params.deliveryAddress && { delivery_address: params.deliveryAddress }),
    ...(params.customerPickupAddress && {
      customer_pickup_address: params.customerPickupAddress,
    }),
    ...(params.userConsent && { user_consent: params.userConsent }),
    ...(params.tipAmount != null && { tip_amount: params.tipAmount }),
    ...(params.promoCode && { promo_code: params.promoCode }),
    // An empty note is still sent, as v1 sends it.
    ...(params.message != null && { message: params.message }),
    ...(params.deliveryStartedAt && {
      delivery_started_at: params.deliveryStartedAt,
    }),
    ...(params.deliveryFinishedAt && {
      delivery_finished_at: params.deliveryFinishedAt,
    }),
    ...(params.isCurbsidePickup != null && {
      is_curbside_pickup: params.isCurbsidePickup,
    }),
    ...(params.isMobile != null && { is_mobile: params.isMobile }),
    ...(params.currencyCode && { currency_code: params.currencyCode }),
    ...(params.deviceId && { j_device_id: params.deviceId }),
    ...(params.headlessCheckoutPartnerId && {
      headless_checkout_partner_id: params.headlessCheckoutPartnerId,
    }),
    ...(params.promotionsOptOut != null && {
      promotions_opt_out: params.promotionsOptOut,
    }),
    ...(params.ageVerification && { age_verification: params.ageVerification }),
    ...(params.medicalIdentification && {
      medical_identification: params.medicalIdentification,
    }),
    ...(params.aeropayPreauthorizationId && {
      aeropay_preauthorization_id: params.aeropayPreauthorizationId,
    }),
    ...(params.aeropayBankAccountId && {
      aeropay_bank_account_id: params.aeropayBankAccountId,
    }),
    ...(params.aeropayAccountVerified != null && {
      aeropay_account_verified: params.aeropayAccountVerified,
    }),
    ...(params.monerisTicket && { moneris_ticket: params.monerisTicket }),
    ...(params.canpayToken && { canpay_token: params.canpayToken }),
    ...(params.strongholdPaymentSourceId && {
      stronghold_payment_source_id: params.strongholdPaymentSourceId,
    }),
    ...(params.squarePaymentToken && {
      square_payment_token: params.squarePaymentToken,
    }),
    ...(params.tags && { tags: params.tags }),
    ...(params.anonymousUserId && {
      anonymous_user_id: params.anonymousUserId,
    }),
    ...(params.userGroupSpecialId != null && {
      user_group_special_id: params.userGroupSpecialId,
    }),
    ...(params.canpayV2remotepayTransaction && {
      canpay_v2remotepay_transaction: params.canpayV2remotepayTransaction,
    }),
    ...(params.payfirmaCardNumber && {
      payfirma_card_number: params.payfirmaCardNumber,
    }),
    ...(params.payfirmaCardExpiryMonth && {
      payfirma_card_expiry_month: params.payfirmaCardExpiryMonth,
    }),
    ...(params.payfirmaCardExpiryYear && {
      payfirma_card_expiry_year: params.payfirmaCardExpiryYear,
    }),
    ...(params.payfirmaCardCvv2 && {
      payfirma_card_cvv2: params.payfirmaCardCvv2,
    }),
    ...(params.payfirmaCardPostalCode && {
      payfirma_card_postal_code: params.payfirmaCardPostalCode,
    }),
  };

  return api.post<CheckoutResponse>(
    cartUrls.checkout(cartUuid),
    body,
    jwt,
    appModeHeader(appMode)
  );
};

export const applyPromoCode = async ({
  cartUuid,
  promoCode,
  jwt,
  appMode,
}: ApplyPromoCodeProps) => {
  const response = await api.post<CartResponse>(
    cartUrls.promoCode(cartUuid),
    { promo_code: promoCode },
    jwt,
    appModeHeader(appMode)
  );
  return response.cart;
};

export const addLoyaltyRedemption = async ({
  cartUuid,
  jwt,
  appMode,
  crmProvider,
  crmRewardId,
  rewardName,
  pointValue,
  reward,
  crmMemberPoints,
  redemptionUrl,
  redemptionChecksum,
}: AddLoyaltyRedemptionProps) => {
  const body = {
    crm_provider: crmProvider,
    crm_reward_id: crmRewardId,
    reward_name: rewardName,
    point_value: pointValue,
    reward,
    crm_member_points: crmMemberPoints,
    redemption_url: redemptionUrl,
    ...(redemptionChecksum !== undefined && {
      redemption_checksum: redemptionChecksum,
    }),
  };

  const response = await api.post<CartResponse>(
    cartUrls.loyaltyRedemptions(cartUuid),
    body,
    jwt,
    appModeHeader(appMode)
  );
  return response.cart;
};

export const removeLoyaltyRedemption = async ({
  cartUuid,
  redemptionId,
  jwt,
  appMode,
}: RemoveLoyaltyRedemptionProps) => {
  await api.delete(
    cartUrls.loyaltyRedemption(cartUuid, redemptionId),
    jwt,
    appModeHeader(appMode)
  );
};
